/**
 * api.js — Typed API client for the FastAPI backend.
 *
 * All requests go through _req() which handles errors uniformly.
 * SSE streams return an EventSource — the caller closes it.
 *
 * Base URL is empty (same origin in production, proxied by Vite in dev).
 */

import { forceLogout, getToken } from './auth.js'

const BASE = '/api'
async function _fetchWithAuthRetry(input, init = {}) {
  let res = await fetch(input, init)
  if (res.status !== 401) return res

  const freshToken = await getToken({ skipCache: true })
  if (!freshToken) return res

  const headers = new Headers(
    (init && init.headers) ||
    (typeof input !== 'string' && input && input.headers) ||
    undefined,
  )
  headers.set('Authorization', `Bearer ${freshToken}`)
  return fetch(input, { ...init, headers })
}

// ── Core fetch wrapper ────────────────────────────────────────────────────

async function _req(method, path, body = undefined, opts = {}) {
  const headers = {}
  let bodyStr

  // Auth: the global fetch wrapper in auth.js injects the Clerk bearer token
  // on all /api requests, so no manual token handling is needed here.

  if (body instanceof FormData) {
    // Let browser set multipart boundary
    bodyStr = body
  } else if (body !== undefined) {
    headers['Content-Type'] = 'application/json'
    bodyStr = JSON.stringify(body)
  }

  const res = await _fetchWithAuthRetry(`${BASE}${path}`, {
    method,
    headers,
    body: bodyStr,
    ...opts,
  })

  if (res.status === 401) {
    forceLogout()
    throw new Error('Session expired — please sign in again.')
  }

  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`
    try { const j = await res.json(); msg = j.detail ?? msg } catch {}
    const err = new Error(msg)
    err.status = res.status
    throw err
  }

  const ct = res.headers.get('content-type') ?? ''
  if (ct.includes('application/json')) return res.json()
  return res
}

// ── Documents ─────────────────────────────────────────────────────────────

/**
 * Upload a PDF file.
 * @param {File} file
 * @returns {Promise<{doc_id: string, page_count: number}>}
 */
export async function uploadDocument(file, opts = {}) {
  const fd = new FormData()
  fd.append('file', file)
  return _uploadDocumentWithXhr(fd, opts)
}

async function _uploadDocumentWithXhr(formData, opts = {}) {
  // A 401 retry would resend the entire multipart body. For large plans that
  // means hundreds of MB uploaded twice, so start with a fresh token instead.
  const token = await getToken({ skipCache: true })
  const res = await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${BASE}/documents`)
    xhr.timeout = opts.timeout ?? 3600000
    xhr.__skipAuthHeader = true
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    if (typeof opts.onProgress === 'function') {
      xhr.upload.addEventListener('progress', opts.onProgress)
    }
    xhr.addEventListener('load', () => {
      resolve({
        status: xhr.status,
        ok: xhr.status >= 200 && xhr.status < 300,
        text: xhr.responseText || '',
      })
    })
    xhr.addEventListener('error', () => reject(new Error('Network error while uploading document')))
    xhr.addEventListener('timeout', () => reject(new Error('Upload timed out')))
    xhr.send(formData)
  })

  if (res.status === 401) {
    forceLogout()
    throw new Error('Session expired - please sign in again.')
  }
  if (!res.ok) {
    let msg = `${res.status}`
    try {
      const parsed = JSON.parse(res.text)
      msg = parsed.detail || msg
    } catch { /* keep status */ }
    throw new Error(msg)
  }
  try {
    return JSON.parse(res.text)
  } catch {
    throw new Error('Upload returned an invalid response')
  }
}

/**
 * Fetch document metadata.
 * @param {string} docId
 * @returns {Promise<{doc_id: string, page_count: number, filename: string}>}
 */
export async function getDocument(docId) {
  return _req('GET', `/documents/${docId}`)
}

export async function getDocumentViewerStatus(docId) {
  return _req('GET', `/documents/${docId}/viewer/status`)
}

export async function getDocumentViewer(docId) {
  return _req('GET', `/documents/${docId}/viewer`)
}

export async function checkDocumentBluebeam(docId) {
  return _req('GET', `/documents/${docId}/bluebeam`)
}

export function documentBluebeamExtractUrl(docId, index) {
  return `${BASE}/documents/${docId}/bluebeam/extract?index=${encodeURIComponent(index)}`
}

export async function extractDocumentBluebeamToDocument(docId, index) {
  return _req('POST', `/documents/${docId}/bluebeam/extract-document?index=${encodeURIComponent(index)}`)
}

export async function prepareDocumentViewer(docId, handlers = {}) {
  const resp = await _fetchWithAuthRetry(`${BASE}/documents/${docId}/viewer/prepare`, { method: 'POST' })
  if (resp.status === 401) { forceLogout(); throw new Error('Session expired') }
  if (!resp.ok) throw new Error(`Viewer prep failed: ${resp.status}`)

  const reader = resp.body.getReader()
  const decoder = new TextDecoder()
  let buf = ''
  let final = null

  while (true) {
    const { done, value } = await reader.read()
    if (value) buf += decoder.decode(value, { stream: true })

    let idx
    while ((idx = buf.indexOf('\n\n')) !== -1) {
      const block = buf.slice(0, idx)
      buf = buf.slice(idx + 2)
      let eventType = 'message'
      let data = ''
      for (const line of block.split('\n')) {
        if (line.startsWith('event: ')) eventType = line.slice(7).trim()
        else if (line.startsWith('data: ')) data = line.slice(6)
      }
      if (!data) continue
      const parsed = JSON.parse(data)
      if (eventType === 'status') handlers.onStatus?.(parsed)
      else if (eventType === 'progress') handlers.onProgress?.(parsed)
      else if (eventType === 'done') final = parsed
      else if (eventType === 'error') throw new Error(parsed.message || 'Viewer prep error')
    }

    if (done) break
  }

  if (!final) throw new Error('No completion event received from viewer preparation')
  return final
}

/**
 * Get a URL to load the original PDF directly (for PDF.js).
 * PDF.js handles its own range requests — no proxying needed.
 * @param {string} docId
 * @returns {string}
 */
export function documentPdfUrl(docId) {
  return `${BASE}/documents/${docId}/file`
}

/**
 * Download a subset of pages from a document as a PDF.
 * @param {string} docId
 * @param {number[]} pageIndices  0-based page indices
 * @param {string} filename       Desired download filename (without .pdf)
 * @returns {Promise<Blob>}
 */
export async function downloadPagesSubset(docId, pageIndices, filename) {
  const params = new URLSearchParams({
    pages: pageIndices.join(','),
    filename,
  })
  const res = await _fetchWithAuthRetry(`${BASE}/documents/${docId}/pages-subset?${params}`)
  if (!res.ok) throw new Error(`pages-subset failed: ${res.status}`)
  return res.blob()
}

/**
 * List the signed-in user's recent uploads for the home-screen menu.
 * @param {number} [days=30]
 * @param {number} [limit=50]
 * @returns {Promise<Array<{doc_id:string, filename:string, page_count:number, created_at:string, ready:boolean}>>}
 */
export async function listRecentJobs(days = 30, limit = 50) {
  // no-store: never serve a browser-cached recent list. A just-deleted doc must
  // not be resurrected from HTTP cache after the manifest write.
  return _req('GET', `/documents/recent?days=${days}&limit=${limit}`, undefined, { cache: 'no-store' })
}

/**
 * Rename a cached document.
 * @param {string} docId
 * @param {string} filename
 * @returns {Promise<{doc_id:string, page_count:number, filename:string, created_at:string}>}
 */
export async function renameDocument(docId, filename) {
  return _req('PATCH', `/documents/${docId}`, { filename })
}

/**
 * Delete a cached document and its stored artifacts.
 * @param {string} docId
 * @returns {Promise<void>}
 */
export async function deleteDocument(docId) {
  await _req('DELETE', `/documents/${docId}`)
}

/**
 * Wake the on-demand GPU worker (it auto-stops when idle to save cost). Called
 * on login so the box is warming up by the time the user acts. Best-effort.
 * @returns {Promise<void>}
 */
export async function wakeWorker() {
  try { await _req('POST', '/worker/wake') } catch { /* non-fatal */ }
}

/**
 * Get the on-demand GPU worker's current state (for a "waking…" indicator).
 * Returns the status object, or null if it can't be reached.
 * @returns {Promise<{enabled:boolean,state:string,inflight?:number}|null>}
 */
export async function workerStatus() {
  try { return await _req('GET', '/worker/status') } catch { return null }
}

/**
 * Flag a document as fully preprocessed so it shows as "ready" in the menu.
 * Best-effort — failures are swallowed.
 * @param {string} docId
 */
export async function markDocumentReady(docId) {
  try { await _req('POST', `/documents/${docId}/ready`) } catch { /* non-fatal */ }
}

// ── Project package: proposals / addendums attached to a plan ─────────────
export async function getProjectPackage(docId) {
  return _req('GET', `/documents/${docId}/package`, undefined, { cache: 'no-store' })
}
export async function addProjectAttachment(docId, file, kind) {
  const fd = new FormData(); fd.append('file', file); fd.append('kind', kind)
  return _req('POST', `/documents/${docId}/package`, fd)
}
export async function replaceProjectAttachment(docId, attId, file) {
  const fd = new FormData(); fd.append('file', file)
  return _req('PUT', `/documents/${docId}/package/${attId}`, fd)
}
export async function removeProjectAttachment(docId, attId) {
  await _req('DELETE', `/documents/${docId}/package/${attId}`)
}
/** Fetch an attachment PDF (auth header injected) and return an object URL. */
export async function openProjectAttachment(docId, attId) {
  const res = await _fetchWithAuthRetry(`${BASE}/documents/${docId}/package/${attId}/file`)
  if (!res.ok) throw new Error('Could not open document')
  const blob = await res.blob()
  return URL.createObjectURL(blob)
}

/** Auth-fetch a PDF and return it as a File (for opening in an in-app tab). */
async function _fetchAsFile(path, filename) {
  const res = await _fetchWithAuthRetry(`${BASE}${path}`)
  if (!res.ok) throw new Error(`Could not fetch document (${res.status})`)
  const blob = await res.blob()
  return new File([blob], filename || 'document.pdf', { type: 'application/pdf' })
}
export async function getProjectAttachmentFile(docId, attId, filename) {
  return _fetchAsFile(`/documents/${docId}/package/${attId}/file`, filename)
}
export async function getSpecFile(specId, filename) {
  return _fetchAsFile(`/spec/${specId}/download`, filename)
}

/**
 * Download a cached document's original PDF as a File (auth header injected by
 * the global fetch wrapper). Used to reopen a job without re-uploading.
 * @param {string} docId
 * @param {string} filename
 * @returns {Promise<File>}
 */
export async function fetchDocumentFile(docId, filename) {
  const res = await _fetchWithAuthRetry(`${BASE}/documents/${docId}/file`)
  if (res.status === 401) { forceLogout(); throw new Error('Session expired') }
  if (!res.ok) throw new Error(`Could not load cached PDF (${res.status})`)
  const blob = await res.blob()
  return new File([blob], filename || 'document.pdf', { type: 'application/pdf' })
}

// ── Jobs ──────────────────────────────────────────────────────────────────

/**
 * Start a pipeline job.
 * @param {string} docId
 * @param {string[]} pipeline  e.g. ['ocr', 'tables', 'layout']
 * @param {number[]|null} pages  Subset of pages, or null for all.
 * @returns {Promise<{job_id: string, status: string}>}
 */
export async function createJob(docId, pipeline, pages = null) {
  return _req('POST', `/documents/${docId}/jobs`, { pipeline, pages })
}

/**
 * Poll job status.
 * @param {string} docId
 * @param {string} jobId
 * @returns {Promise<{status: string, progress: {done: number, total: number}, errors: string[]}>}
 */
export async function getJob(docId, jobId) {
  return _req('GET', `/documents/${docId}/jobs/${jobId}`)
}

/**
 * Open an SSE stream for a job.
 * Events: { event: 'page_done', page: number, artifacts: string[] }
 *         { event: 'job_done' }
 *
 * @param {string} docId
 * @param {string} jobId
 * @param {{ onPageDone: (page: number, artifacts: string[]) => void,
 *            onJobDone: () => void,
 *            onError: (err: Event) => void }} handlers
 * @returns {EventSource}  Call .close() to stop.
 */
export function openJobStream(docId, jobId, { onPageDone, onJobDone, onError }) {
  const url = `${BASE}/documents/${docId}/jobs/${jobId}/stream`
  const es = new EventSource(url)

  es.addEventListener('page_done', (e) => {
    const data = JSON.parse(e.data)
    onPageDone?.(data.page, data.artifacts)
  })

  es.addEventListener('job_done', () => {
    onJobDone?.()
    es.close()
  })

  es.onerror = (err) => {
    onError?.(err)
    es.close()
  }

  return es
}

// ── Artifacts ─────────────────────────────────────────────────────────────

/**
 * Fetch all artifacts for a page.
 * Returns null if not yet produced (404).
 * @param {string} docId
 * @param {number} pageIdx 0-indexed
 * @returns {Promise<{text: object[], tables: object[], layout: object[]}|null>}
 */
export async function getPageArtifacts(docId, pageIdx) {
  try {
    return await _req('GET', `/documents/${docId}/pages/${pageIdx}/artifacts`)
  } catch (e) {
    if (e.message.startsWith('404')) return null
    throw e
  }
}

// ── Annotations ───────────────────────────────────────────────────────────

/**
 * Save annotations for a page (full replace).
 * @param {string} docId
 * @param {number} pageIdx
 * @param {Array<{rect: object, label: string, note: string}>} boxes
 * @returns {Promise<{annotation_id: string}>}
 */
export async function saveAnnotations(docId, pageIdx, boxes) {
  return _req('POST', `/documents/${docId}/pages/${pageIdx}/annotations`, { boxes })
}

/**
 * Fetch all annotations for a page.
 * @param {string} docId
 * @param {number} pageIdx
 * @returns {Promise<Array<{annotation_id: string, rect: object, label: string, created_at: string}>>}
 */
export async function getAnnotations(docId, pageIdx) {
  try {
    return await _req('GET', `/documents/${docId}/pages/${pageIdx}/annotations`)
  } catch {
    return []
  }
}

/**
 * Classify every page in a document into categories.
 * Streams SSE progress events, calling onProgress({done, total}) for each batch.
 * Returns the final result {pages, total} when complete.
 * @param {string} docId
 * @param {function} [onProgress] - called with {done, total} after each batch
 * @returns {Promise<{pages: Array<{page: number, categories: string[], top: string, confidence: number}>, total: number}>}
 */
export async function classifyDocument(docId, onProgress) {
  const resp = await _fetchWithAuthRetry(`${BASE}/classify/document/${docId}`, { method: 'POST' })
  if (resp.status === 401) { forceLogout(); throw new Error('Session expired') }
  if (!resp.ok) throw new Error(`Classify failed: ${resp.status}`)

  const reader = resp.body.getReader()
  const decoder = new TextDecoder()
  let buf = ''
  let finalResult = null

  while (true) {
    const { done, value } = await reader.read()
    if (value) buf += decoder.decode(value, { stream: true })

    // Parse SSE events from buffer
    let idx
    while ((idx = buf.indexOf('\n\n')) !== -1) {
      const block = buf.slice(0, idx)
      buf = buf.slice(idx + 2)
      let eventType = 'message'
      let data = ''
      for (const line of block.split('\n')) {
        if (line.startsWith('event: ')) eventType = line.slice(7).trim()
        else if (line.startsWith('data: ')) data = line.slice(6)
      }
      if (!data) continue
      const parsed = JSON.parse(data)
      if (eventType === 'progress' && onProgress) {
        onProgress(parsed)
      } else if (eventType === 'result') {
        finalResult = parsed
      } else if (eventType === 'error') {
        throw new Error(parsed.message || 'Classification error')
      }
    }

    if (done) break
  }

  if (!finalResult) throw new Error('No result received from classification')
  return finalResult
}

// ── Preprocessing (light graph builder) ───────────────────────────────────

/**
 * Run light_graph_builder preprocessing for a document.
 * Streams SSE progress. Calls handlers as events arrive.
 * Resolves with the final { done, total, cached } when complete.
 * @param {string} docId
 * @param {{ onStatus?: (s:{phase:string,message:string})=>void,
 *           onProgress?: (p:{done:number,total:number})=>void }} [handlers]
 * @returns {Promise<{done:number,total:number,cached:boolean}>}
 */
export async function preprocessDocument(docId, handlers = {}) {
  // Auth header is injected by the global fetch wrapper (auth.js).
  const resp = await _fetchWithAuthRetry(`${BASE}/preprocess/document/${docId}`, { method: 'POST' })
  if (resp.status === 401) { forceLogout(); throw new Error('Session expired') }
  if (!resp.ok) throw new Error(`Preprocess failed: ${resp.status}`)

  const reader = resp.body.getReader()
  const decoder = new TextDecoder()
  let buf = ''
  let final = null
  let failure = null

  try {
    outer: while (true) {
      const { done, value } = await reader.read()
      if (value) buf += decoder.decode(value, { stream: true })

      let idx
      while ((idx = buf.indexOf('\n\n')) !== -1) {
        const block = buf.slice(0, idx)
        buf = buf.slice(idx + 2)
        let eventType = 'message'
        let data = ''
        for (const line of block.split('\n')) {
          if (line.startsWith('event: ')) eventType = line.slice(7).trim()
          else if (line.startsWith('data: ')) data = line.slice(6)
        }
        if (!data) continue
        const parsed = JSON.parse(data)
        if (eventType === 'status') handlers.onStatus?.(parsed)
        else if (eventType === 'progress') handlers.onProgress?.(parsed)
        else if (eventType === 'done') { final = parsed; break outer }
        else if (eventType === 'error') { failure = new Error(parsed.message || 'Preprocess error'); break outer }
      }

      if (done) break
    }
  } catch {
    // Network/stream drop (broker blip, proxy idle-timeout). The worker keeps
    // running server-side -- never go blind: poll status until it lands.
    handlers.onStatus?.({ phase: 'reconnect', message: 'Connection dropped \u2014 still processing on the server\u2026' })
    return _pollPreprocessUntilDone(docId, handlers)
  }

  if (failure) throw failure                 // a genuine task failure -- surface it
  if (final) return final
  // Stream closed cleanly but no 'done' event -- fall back to polling.
  return _pollPreprocessUntilDone(docId, handlers)
}

/** Cheap status probe: {done, pages, running}. `done` flips true once the
 *  consolidated graphs.json exists in storage. */
export async function preprocessStatus(docId) {
  return _req('GET', `/preprocess/document/${docId}/status`)
}

// The preprocess task runs server-side, independent of the SSE connection. If
// the stream drops we must NOT strand the user on a frozen bar or a false
// "failed" -- poll the status endpoint until the plan is ready.
async function _pollPreprocessUntilDone(docId, handlers = {}, { intervalMs = 5000, timeoutMs = 45 * 60 * 1000 } = {}) {
  const start = Date.now()
  while (Date.now() - start < timeoutMs) {
    await new Promise((r) => setTimeout(r, intervalMs))
    let st = null
    try { st = await preprocessStatus(docId) } catch { continue }
    if (st && st.done) {
      handlers.onProgress?.({ done: st.pages, total: st.pages })
      return { done: st.pages, total: st.pages, cached: true }
    }
    handlers.onStatus?.({ phase: 'processing', message: 'Still processing on the server\u2026' })
  }
  throw new Error('Preprocessing timed out \u2014 not ready after 45 minutes')
}

/**
 * Get preprocessing status for a document.
 * @param {string} docId
 * @returns {Promise<{done:boolean, pages:number, running:boolean}>}
 */
export async function getPreprocessStatus(docId) {
  return _req('GET', `/preprocess/document/${docId}/status`)
}

/**
 * Get page classification derived from light graph output.
 * @param {string} docId
 * @returns {Promise<{pages: Array<{page:number, categories:string[], top:string, confidence:number}>, total:number}>}
 */
export async function getPreprocessClassification(docId) {
  return _req('GET', `/preprocess/document/${docId}/classification`)
}

/**
 * Get table_callout groups derived from light graph output.
 * @param {string} docId
 * @returns {Promise<{callouts: Array<{text:string, key:string, count:number, pages:number[], instances:Array<{page:number, bbox:number[], img_w:number, img_h:number, conf:number, text:string}>}>, total:number}>}
 */
export async function getCallouts(docId) {
  return _req('GET', `/preprocess/document/${docId}/callouts`)
}

// ── Chat (OpenClaw agent) ─────────────────────────────────────────────────

/**
 * Whether the agent boundary is wired up on the server (does not reach the GPU).
 * @returns {Promise<{configured:boolean}>}
 */
export async function chatStatus() {
  return _req('GET', '/chat/status')
}

/**
 * The user's per-project chat histories to choose from.
 * @returns {Promise<{sessions: Array<{session_id:string, project_id:string, title:string, has_history:boolean, created_at:string|null}>}>}
 */
export async function chatSessions() {
  return _req('GET', '/chat/sessions')
}

/**
 * Load a page of a session/project transcript (newest-anchored). Pass `before`
 * (an offset cursor from a prior response) to page older entries.
 * @param {string} sessionId
 * @param {{ limit?: number, before?: number|null }} [opts]
 * @returns {Promise<{session_id:string, entries: Array<{ts:string, role:string, text:string}>, total:number, offset:number, has_more:boolean}>}
 */
export async function chatHistory(sessionId, opts = {}) {
  const p = new URLSearchParams({ session_id: sessionId })
  if (opts.limit != null) p.set('limit', String(opts.limit))
  if (opts.before != null) p.set('before', String(opts.before))
  return _req('GET', `/chat/history?${p.toString()}`)
}

/**
 * Hide a session's existing transcript from the UI going forward. The data is
 * NOT deleted server-side -- the agent still reads the full transcript, so the
 * model's memory is unchanged; only the user's history view is trimmed.
 * @param {string} sessionId
 */
export async function clearChat(sessionId) {
  return _req('POST', '/chat/clear', { session_id: sessionId })
}

/**
 * The project's persisted extracted Excel tables.
 * @returns {Promise<{tables: Array<{export_id:string, page_num:number, ext:string, filename:string, created_at:string}>}>}
 */
export async function listSavedTables(docId) {
  return _req('GET', `/detection/exports/${docId}`)
}

/** Remove one saved table from a project (index entry + stored file). */
export async function deleteSavedTable(docId, exportId) {
  return _req('DELETE', `/detection/exports/${docId}/${exportId}`)
}

/**
 * Save (or overwrite, when export_id is given) a live chat table into the
 * project's saved-tables store — the SAME place Pull-Table exports go.
 * @param {string} docId
 * @param {{export_id?:string, name?:string, page_num?:number, columns:string[], rows:string[][], page_refs?:Object}} payload
 * @returns {Promise<{export_id:string, name:string, filename:string}>}
 */
export async function saveLiveTable(docId, payload) {
  return _req('POST', `/detection/live-table/${docId}`, payload)
}

/** Fetch a saved table's cells so the chat can re-open it as a live table. */
export async function getLiveTableCells(docId, exportId) {
  return _req('GET', `/detection/live-table/${docId}/${exportId}`)
}

// ── Specifications (per-user constant library) ─────────────────────────────

/**
 * The signed-in user's specification documents (constant context for every
 * plan chat and the home assistant).
 * @returns {Promise<{specs: Array<{spec_id:string, filename:string, pages:number, size:number, uploaded_at:string}>}>}
 */
export async function listSpecs() {
  return _req('GET', '/spec')
}

/** Upload a spec PDF to the user's library. */
export async function uploadSpec(file) {
  const form = new FormData()
  form.append('file', file)
  const resp = await _fetchWithAuthRetry(`${BASE}/spec`, { method: 'POST', body: form })
  if (resp.status === 401) { forceLogout(); throw new Error('Session expired') }
  if (!resp.ok) {
    let msg = `Upload failed: ${resp.status}`
    try { const j = await resp.json(); msg = j.detail ?? msg } catch {}
    throw new Error(msg)
  }
  return resp.json()
}

/** Remove one spec from the user's library. */
export async function deleteSpec(specId) {
  return _req('DELETE', `/spec/${specId}`)
}

// ── Ingest (drop-to-route: plan vs spec) ───────────────────────────────────

/**
 * Ask the GPU vision model whether a dropped document (its first-page image)
 * is a plan or a spec.
 * @param {string} image  data URL / base64 of the first page
 * @param {string} [filename]
 * @returns {Promise<{type: 'plan'|'spec'|'unknown'}>}
 */
export async function classifyDoc(image, filename) {
  return _req('POST', '/ingest/classify', { image, filename })
}

/**
 * Send one chat turn to the OpenClaw agent, streaming typed SSE events back.
 * The server injects the verified user id; the browser never sends it.
 *
 * @param {string} message
 * @param {{ sessionId?: string, projectId?: string, page?: number, view?: string, viewing?: object, signal?: AbortSignal }} [opts]
 * @param {{ onToken?: (t:{kind:string,text:string})=>void,
 *           onTool?: (t:{message:string})=>void,
 *           onStatus?: (s:{message:string})=>void }} [handlers]
 * @returns {Promise<string>}  the final answer text
 */
export async function sendChatMessage(message, opts = {}, handlers = {}) {
  const body = { message, session_id: opts.sessionId ?? null, project_id: opts.projectId ?? null }
  // Where the user is right now (project chats only), so the agent has context.
  if (opts.page != null) body.page = opts.page
  if (opts.view != null) body.view = opts.view
  if (opts.viewing != null) body.viewing = opts.viewing
  const resp = await _fetchWithAuthRetry(`${BASE}/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal: opts.signal,
  })
  if (resp.status === 401) { forceLogout(); throw new Error('Session expired') }
  if (!resp.ok) {
    let msg = `Chat failed: ${resp.status}`
    try { const j = await resp.json(); msg = j.detail ?? msg } catch {}
    throw new Error(msg)
  }

  const reader = resp.body.getReader()
  const decoder = new TextDecoder()
  let buf = ''
  let answer = null

  while (true) {
    const { done, value } = await reader.read()
    if (value) buf += decoder.decode(value, { stream: true })

    let idx
    while ((idx = buf.indexOf('\n\n')) !== -1) {
      const block = buf.slice(0, idx)
      buf = buf.slice(idx + 2)
      let eventType = 'message'
      let data = ''
      for (const line of block.split('\n')) {
        if (line.startsWith('event: ')) eventType = line.slice(7).trim()
        else if (line.startsWith('data: ')) data = line.slice(6)
      }
      if (!data) continue
      const parsed = JSON.parse(data)
      if (eventType === 'token') handlers.onToken?.(parsed)
      else if (eventType === 'tool') handlers.onTool?.(parsed)
      else if (eventType === 'status') handlers.onStatus?.(parsed)
      else if (eventType === 'done') answer = parsed.answer ?? ''
      else if (eventType === 'error') throw new Error(parsed.message || 'Agent error')
    }

    if (done) break
  }

  if (answer === null) throw new Error('No answer received from the agent')
  return answer
}
